<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { useWorkspaceStore } from '@/modules/workspace/stores/workspace-store'
import TheAppBanners from '@/modules/shared/components/the-app-banners.vue'
import TheSidenav from '@/modules/shared/components/the-sidenav.vue'
import { rails_url } from '@/modules/shared/utils/rails'

defineProps<{ hideSidenav?: boolean; fillViewport?: boolean; edgeToEdge?: boolean }>()

const route = useRoute()
const authStore = useAuthStore()
const workspaceStore = useWorkspaceStore()

// Gray background for migrated entity pages (route-name list, epic 107) plus
// any route that opts in via `meta.migratedDesign` (epic 109, e.g. files).
const isGrayBgPage = computed(() => {
  if (['spv', 'fund', 'spvs', 'funds', 'gps'].includes(String(route.params.entity_type ?? ''))) return true
  if (route.params.associated_entity_id) return true
  if (Boolean(route.meta?.migratedDesign)) return true
  if (
    [
      'companies.profile',
      'companies.activity',
      'companies.list',
      'companies.applications',
      'companies.discussions',
      'companies.account',
    ].includes(String(route.name ?? ''))
  )
    return true

  return false
})

// Overview and the account settings page (#2991) use the full content width;
// every other page renders inside the 95% column.
const isFullWidthEntityPage = computed(
  () => isGrayBgPage.value && ['investing.entity-overview', 'investing.entity.account'].includes(String(route.name)),
)

const isBannerVisible = computed(
  () => workspaceStore.current.options.is_setup_mode_enabled && authStore.is_site_or_group_admin,
)
</script>

<template>
  <!-- v-recede-surface: the recede target for the Avgrund push in
         v-pop-up-modal — a fixed inset-0 wrapper around ALL app chrome
         (banners, gradient bar, impersonation chip, shell) so the whole
         app retreats as one card over the abyss. Fixed children re-anchor
         to this box, which IS the viewport box, so resting geometry is
         unchanged. Receding #app itself does not work — it is a
         transparent in-flow shell that would clip this layer away. -->
  <div class="v-recede-surface fixed inset-0">
    <!-- Global banners: toasts, setup-mode banner, gradient bar (all users + viewports) -->
    <TheAppBanners />
    <!-- Impersonation banner (mobile) -->
    <div
      v-if="authStore.logged_in_as"
      class="fixed right-0 top-0 z-[51] sm:hidden"
      style="background-color: #ff853a; font-size: 11px; color: #fff; padding: 2px 4px"
    >
      <div style="float: right">
        <a
          style="padding: 2px 4px 1px 4px; background-color: rgba(255, 255, 255, 0.3)"
          :href="`${rails_url()}/login-as/${authStore.current_user.original_user_id}?new_dashboard=true`"
          >Stop</a
        >
      </div>
      <div style="float: right; margin-right: 10px">Viewing as {{ authStore.current_user.name }} · read-only</div>
    </div>
    <div
      class="fixed inset-0 flex overflow-hidden bg-white"
      :class="[!authStore.is_company_user ? 'top-[58px]' : 'top-0', isBannerVisible ? 'sm:top-8' : 'sm:top-0']"
    >
      <TheSidenav v-if="!hideSidenav && !authStore.is_company_user" />
      <main
        :class="[
          'h-full flex-grow',
          fillViewport ? 'overflow-hidden' : 'overflow-y-auto',
          isGrayBgPage ? 'bg-gray-50' : 'bg-white',
          { 'mt-8 sm:mt-0': isBannerVisible },
        ]"
      >
        <!-- fillViewport: the page owns its height (an h-full flex column)
             and manages its own internal scrolling — e.g. the pipeline
             board's per-column scrollers. Deliberately no overflow-x-hidden
             here: with one axis non-visible, CSS computes the other to auto,
             and a page-level vertical scroller would quietly return.
             edgeToEdge: opts the fill page out of the app gutter — for
             surfaces whose scroller should own the full width (e.g. the
             pipeline board); pair with page-level padding on any
             non-scrolling rows that must keep the gutter alignment. -->
        <div v-if="fillViewport" class="flex h-full flex-col px-0.5" :class="edgeToEdge ? '' : 'sm:mx-12'">
          <slot></slot>
        </div>
        <div
          v-else
          :class="[
            'm-auto my-8 overflow-x-hidden px-0.5 py-6 sm:mx-12 sm:w-[inherit] sm:overflow-x-visible sm:px-0',
            { 'w-[95%]': !isFullWidthEntityPage },
          ]"
        >
          <slot></slot>
        </div>
      </main>
    </div>
  </div>
</template>
